import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Modal from '../../../../design-system/modal/Modal';
import Button from '../../../../design-system/button/Button';
import { acknowledgeBadgePopup, trackBadgeShare } from '../../../../../actions/social-features';
import { shareToLinkedIn } from '../../../../../utils/share-to-linkedin';

import styles from './SocialFeaturesPopup.module.scss';

/**
 * Social Features Popup - Modal that shows notifications for social accountability features
 * Different content is shown based on which feature the user is enrolled in
 */
const SocialFeaturesPopup = ({ isOpen, onClose, featureType, socialFeaturesData }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    // If popup is shown, store the timestamp in localStorage
    // to prevent showing it again in the same day
    if (isOpen) {
      if (featureType === 'peer-milestones' || featureType === 'streak-reminders') {
        const today = new Date().toLocaleDateString();
        localStorage.setItem(`${featureType}-popup-shown`, today);
      } else if (featureType === 'achievement-badges' && socialFeaturesData?.badgeId) {
        // Acknowledge badge popup as soon as it's displayed
        dispatch(acknowledgeBadgePopup(socialFeaturesData.badgeId));
      }
    }
  }, [isOpen, featureType, socialFeaturesData, dispatch]);

  const handlePrimaryAction = () => {
    if (featureType === 'peer-milestones') {
      // Navigate to bookings page
      history.push('/bookings');
    } else if (featureType === 'achievement-badges') {
      // Find the badge with the matching ID from the badges array
      const { badgeId, badges } = socialFeaturesData || { badges: [] };
      const badgeToShare = badges.find((badge) => badge.id === badgeId);

      if (badgeToShare) {
        const badgeData = {
          badgeId: badgeToShare.id,
          badgeName: badgeToShare.name,
          badgeDescription: badgeToShare.description,
          badgeTotal: badgeToShare.total
        };

        // Share to LinkedIn - no need to acknowledge badge again, as it was already done when the popup opened
        shareToLinkedIn(badgeData, dispatch, null, trackBadgeShare);
      } else {
        console.error('Badge not found with ID:', badgeId);
      }

      // Close the popup
      onClose();
    } else {
      // For streak reminders, just close the modal
      onClose();
    }
  };

  // Get peer milestone message based on number of teammates
  const getPeerMilestoneMessage = (teamCount) => {
    if (teamCount === 0) {
      return 'None of your teammates have booked sessions this week. Be the first and set a great example!';
    } else if (teamCount === 1) {
      return '1 teammate has already booked a session this week. Want to join them?';
    } else {
      return `${teamCount} teammates already booked their sessions this week. Want to join them?`;
    }
  };

  // Get peer milestone heading based on number of teammates
  const getPeerMilestoneHeading = (teamCount) => {
    if (teamCount === 0) {
      return 'Lead The Way!';
    } else if (teamCount === 1) {
      return 'Keep The Momentum Going!';
    } else {
      return 'Keeping The Momentum Going!';
    }
  };

  // Get peer milestone action button text based on number of teammates
  const getPeerMilestoneButtonText = (teamCount) => {
    if (teamCount === 0) {
      return 'Be The First To Book';
    } else {
      return 'Book My Next Session';
    }
  };

  // Determine content based on feature type
  const renderContent = () => {
    if (featureType === 'peer-milestones') {
      const { teammatesNumber } = socialFeaturesData || { teammatesNumber: 0 };
      return (
        <div className={styles.popupContent}>
          <div className={styles.badgeIcon}>
            <div className={styles.placeholderIcon}>👥</div>
          </div>
          <h2>{getPeerMilestoneHeading(teammatesNumber)}</h2>
          <p>{getPeerMilestoneMessage(teammatesNumber)}</p>
          <div className={styles.buttonsWrapper}>
            <Button outfit="primaryButton" onClick={handlePrimaryAction}>
              {getPeerMilestoneButtonText(teammatesNumber)}
            </Button>
          </div>
        </div>
      );
    } else if (featureType === 'achievement-badges') {
      const { badgeId, badges = [] } = socialFeaturesData || {};
      // Find the badge with the matching ID
      const badgeToDisplay = badges.find((badge) => badge.id === badgeId);

      if (!badgeToDisplay) {
        console.error('Badge not found with ID:', badgeId);
        return null;
      }

      const { name, total, iconUrl } = badgeToDisplay;

      return (
        <div className={styles.popupContent}>
          <h2>You Earned a New Badge!</h2>
          <div className={styles.badgeContainer}>
            <div className={styles.badgeIcon}>
              {iconUrl ? <img src={iconUrl} alt={name} className={styles.badgeImage} /> : <div className={styles.placeholderIcon}>🏆</div>}
            </div>
            <div className={styles.badgeInfo}>
              <p className={styles.badgeTitle}>{name}</p>
              <p>Completed {total} Language Practices — great momentum!</p>
            </div>
          </div>
          <div className={styles.buttonsWrapper}>
            <Button outfit="primaryButton" onClick={handlePrimaryAction}>
              Share on LinkedIn
            </Button>
            <Button outfit="secondaryButton" onClick={onClose} className={styles.secondaryButton}>
              Maybe Later
            </Button>
          </div>
        </div>
      );
    } else if (featureType === 'streak-reminders') {
      const { streakDays } = socialFeaturesData || { streakDays: 3 };
      return (
        <div className={styles.popupContent}>
          <div className={styles.badgeIcon}>
            <div className={styles.placeholderIcon}>🔥</div>
          </div>
          <h2>You&apos;re on a {streakDays}-day streak!</h2>
          <p>
            You&apos;ve been showing up — and it&apos;s paying off.
            <br />
            <span className={styles.italic}>(Weekends don&apos;t count, so take your time!)</span>
          </p>
          <div className={styles.buttonsWrapper}>
            <Button outfit="primaryButton" onClick={handlePrimaryAction}>
              Continue Studying
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  // Only render if isOpen is true
  if (!isOpen) return null;

  return (
    <Modal
      loading={false}
      content={
        <div className={styles.socialFeaturesPopupWrapper} data-testid="social-features-popup">
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
          {renderContent()}
        </div>
      }
    />
  );
};

SocialFeaturesPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  featureType: PropTypes.oneOf(['peer-milestones', 'achievement-badges', 'streak-reminders']).isRequired,
  socialFeaturesData: PropTypes.object
};

const mapStateToProps = (state) => ({
  socialFeaturesData: state.socialFeaturesData?.data
});

export default connect(mapStateToProps)(SocialFeaturesPopup);
